import React from 'react';
import { useSparkRunStore, useStatusBarStore } from 'stores';
import {
  InfoCard,
  SparkDrawer,
  SparkScrollableGridContainer,
} from 'sharedComponents/UI';
import SparRunFormContainer from './SparRunFormContainer';
import { ReactFlowProvider } from 'reactflow';
import { useSparkRunByID } from 'hooks/SparkRuns/useSparkRunByID';
import SparkRunToolbar from 'components/common/SparkOutputBoard/BoardToolBar/SparkRunToolbar';
import SparkRunResultsBoardListViewContainer from './SparkRunResultsBoardListViewContainer';
import useExpandAppContentConfig from 'utilities/styles';
import { useBoardNodesByBoardID } from 'hooks/Board';
import { useSparksByID } from 'hooks/spark';
import OutputRatingPanel from './sparkRunOutput/OutputRatingPanel';
import { Box } from '@mui/material';
import { BoardNodeTollBarShimmer } from './SparkRunningProgressScreen';

type SparkRunResultsProps = {
  collectiveSparkRunIDs: string[];
  addToSparkRunIDsCollection: (sparkRunID: string) => void;
  sparkID: string;
};

function SparkRunResults({
  collectiveSparkRunIDs,
  addToSparkRunIDsCollection,
  sparkID,
}: SparkRunResultsProps) {
  const { spark } = useSparksByID(sparkID);
  const {
    boardNodes: fallbackBoardNodes,
    isLoading: isLoadingFallbackNodesBoardNodes,
  } = useBoardNodesByBoardID(spark?.attributes.outputBoardID);
  const isRunning = useStatusBarStore((state) => state.isRunning);
  const isComplete = useStatusBarStore((state) => state.isComplete);
  const isFormSideBarOpen = useSparkRunStore(
    (state) => state.isFormSideBarOpen
  );

  const toggleFormSidBar = useSparkRunStore((state) => state.toggleFormSidBar);
  const selectedSparkRunID = useSparkRunStore(
    (state) => state.selectedSparkRunID
  );
  const isVisible = useStatusBarStore(
    React.useCallback((state) => state.isVisible, [])
  );
  const { expandAppClasses } = useExpandAppContentConfig();

  const { sparkRun, isLoading: isLoadingSparkRun } = useSparkRunByID(
    selectedSparkRunID,
    !!isComplete
  );
  const { boardNodes } = useBoardNodesByBoardID(
    sparkRun?.attributes.outputBoardID
  );
  const canReview = sparkRun?.meta.user.permissions?.canReview || true;
  const [panelHidden, setPanelHidden] = React.useState(!canReview || false);
  const { isLoading: isLoadingBoardNodes } = useBoardNodesByBoardID(
    sparkRun?.attributes.outputBoardID
  );
  const [isHeaderVisible, setIsHeaderVisible] = React.useState(false);

  React.useEffect(() => {
    const headerElement = document.querySelector(`.${expandAppClasses.header}`);

    if (headerElement) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setIsHeaderVisible(entry.contentRect.height > 0);
        }
      });

      resizeObserver.observe(headerElement);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [expandAppClasses.header]);
  const handleHide = () => {
    setPanelHidden(true);
  };

  const isLoading =
    isLoadingBoardNodes ||
    isLoadingSparkRun ||
    isLoadingFallbackNodesBoardNodes;

  const isParentLoading = isLoading || !!isRunning || !isComplete;

  const containerHeight = React.useMemo(() => {
    return isVisible
      ? `calc(100vh - 11rem + ${isHeaderVisible ? 0 : 7}rem)`
      : `calc(100vh - 7rem + ${isHeaderVisible ? 0 : 6}rem)`;
  }, [isVisible, isHeaderVisible]);

  return (
    <SparkScrollableGridContainer
      item
      container
      height={containerHeight}
      justifySelf='center'
      flex={1}
      header={
        <>
          {isParentLoading ? (
            <Box width='100%'>
              <InfoCard
                info='You can navigate away'
                cardStyles={{
                  maxHeight: '4rem',
                  border: 'none',
                }}
                infoStyles={{
                  padding: 2,
                  paddingY: 2.5,
                  alignItems: 'center',
                  margin: 0,
                }}
              />
              <BoardNodeTollBarShimmer />
            </Box>
          ) : (
            <Box width='100%'>
              <OutputRatingPanel
                panelHidden={panelHidden}
                handleHide={handleHide}
                sparkID={sparkID}
                ShowCloseIcon={true}
              />
              <SparkRunToolbar
                boardID={sparkRun?.attributes.outputBoardID || ''}
                toggleSidebar={toggleFormSidBar}
                open={isFormSideBarOpen}
                collectiveSparkRunIDs={collectiveSparkRunIDs}
                containerStyles={{
                  height: '4rem',
                  alignItems: 'center',
                }}
              />
            </Box>
          )}
        </>
      }
      headerStyle={{
        border: 'none',
        height: panelHidden ? '4rem' : '9rem',
        padding: 0,
        marginTop: 0.5,
      }}
      scrollableContainerStyle={{
        padding: 0,
        ...(!isParentLoading && {
          '& .run_page_shadow': {
            display: 'none',
          },
        }),
      }}
    >
      <ReactFlowProvider>
        <SparkRunResultsBoardListViewContainer
          boardNodes={boardNodes || fallbackBoardNodes || []}
          isParentLoading={isParentLoading}
        />
      </ReactFlowProvider>
      <SparkDrawer
        open={isFormSideBarOpen}
        top='6rem'
        drawerClasses={expandAppClasses.sidebar}
      >
        <SparRunFormContainer
          addToSparkRunIDsCollection={addToSparkRunIDsCollection}
          actionContainerStyles={{ zIndex: 1 }}
        />
      </SparkDrawer>
    </SparkScrollableGridContainer>
  );
}

export default SparkRunResults;
